import React from 'react';
import { FactoryIcon, ClockIcon, ZapIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { useTranslation } from '../../lib/i18n';
interface AvailabilityDeliveryProps {
  onRequestQuote: () => void;
}
export function AvailabilityDelivery({
  onRequestQuote
}: AvailabilityDeliveryProps) {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  return <section className="py-20 bg-dark-bg border-y border-dark-border" data-id="element-143">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-144">
        <div className="text-center mb-10" data-id="element-145">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3" data-id="element-146">
            {en ? <>
                Availability and{' '}
                <span className="text-brand-yellow" data-id="element-147">delivery</span>
              </> : ru ? <>
                Наличие и <span className="text-brand-yellow" data-id="element-148">доставка</span>
              </> : <>
                Dostupnost i <span className="text-brand-yellow" data-id="element-149">isporuka</span>
              </>}
          </h2>
        </div>

        <div className="bg-dark-surface border border-dark-border rounded-industrial p-6 md:p-8 mb-8" data-id="element-150">
          <p className="text-gray-300 text-base md:text-lg leading-relaxed" data-id="element-151">
            {en ? <>
                Due to high demand, stock quantities are{' '}
                <span className="text-white font-semibold" data-id="element-152">
                  limited and subject to daily changes
                </span>
                . For sold-out models, the standard lead time for import and
                delivery directly from production (with a guaranteed maximum
                capacity of a new battery) is{' '}
                <span className="text-brand-yellow font-bold" data-id="element-153">4–8 weeks</span>.
              </> : ru ? <>
                Из-за высокого спроса количество на складе{' '}
                <span className="text-white font-semibold" data-id="element-154">
                  ограничено и может меняться ежедневно
                </span>
                . Для распроданных моделей стандартный срок импорта и поставки
                напрямую с производства (с гарантированной максимальной ёмкостью
                новой батареи) составляет{' '}
                <span className="text-brand-yellow font-bold" data-id="element-155">4–8 недель</span>.
              </> : <>
                Zbog visoke potražnje, količine na stanju su{' '}
                <span className="text-white font-semibold" data-id="element-156">
                  ograničene i podložne promenama na dnevnom nivou
                </span>
                . Za modele koji se rasprodaju, standardni rok za uvoz i
                isporuku direktno iz proizvodnje (uz garantovan maksimalni
                kapacitet nove baterije) iznosi{' '}
                <span className="text-brand-yellow font-bold" data-id="element-157">4–8 nedelja</span>
                .
              </>}
          </p>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mt-4" data-id="element-158">
            {en ? <>
                Contact us for{' '}
                <span className="text-white font-semibold" data-id="element-159">
                  current availability and reservations
                </span>
                .
              </> : ru ? <>
                Свяжитесь с нами для уточнения{' '}
                <span className="text-white font-semibold" data-id="element-160">
                  актуального наличия и бронирования
                </span>
                .
              </> : <>
                Kontaktirajte nas za{' '}
                <span className="text-white font-semibold" data-id="element-161">
                  trenutno stanje i rezervaciju
                </span>
                .
              </>}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-id="element-162">
          <div className="flex items-start gap-3 bg-dark-surface/60 border border-dark-border/60 rounded-industrial p-4" data-id="element-163">
            <FactoryIcon className="w-6 h-6 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-164" />
            <div data-id="element-165">
              <p className="text-white font-semibold text-sm mb-1" data-id="element-166">
                {en ? 'Direct from production' : ru ? 'Напрямую с производства' : 'Direktno iz proizvodnje'}
              </p>
              <p className="text-gray-400 text-xs" data-id="element-167">
                {en ? 'Maximum battery capacity, full motor power.' : ru ? 'Максимальная ёмкость аккумулятора, полная мощность мотора.' : 'Maksimalni kapacitet baterije, puna snaga motora.'}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 bg-dark-surface/60 border border-dark-border/60 rounded-industrial p-4" data-id="element-168">
            <ClockIcon className="w-6 h-6 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-169" />
            <div data-id="element-170">
              <p className="text-white font-semibold text-sm mb-1" data-id="element-171">
                {en ? '4–8 weeks lead time' : ru ? 'Срок поставки 4–8 недель' : '4–8 nedelja isporuka'}
              </p>
              <p className="text-gray-400 text-xs" data-id="element-172">
                {en ? 'For models not currently in stock.' : ru ? 'Для моделей, которых нет в наличии.' : 'Za modele koji nisu na stanju.'}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 bg-dark-surface/60 border border-dark-border/60 rounded-industrial p-4" data-id="element-173">
            <ZapIcon className="w-6 h-6 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-174" />
            <div data-id="element-175">
              <p className="text-white font-semibold text-sm mb-1" data-id="element-176">
                {en ? 'Quick response' : ru ? 'Быстрый ответ' : 'Brz odgovor'}
              </p>
              <p className="text-gray-400 text-xs" data-id="element-177">
                {en ? 'We reply to all inquiries within 24h.' : ru ? 'Отвечаем на все запросы в течение 24 ч.' : 'Odgovaramo na sve upite u roku od 24h.'}
              </p>
            </div>
          </div>
        </div>

        <div className="text-center" data-id="element-178">
          <Button variant="primary" size="lg" onClick={onRequestQuote} data-id="element-179">
            {en ? 'Check availability' : ru ? 'Проверить наличие' : 'Proverite dostupnost'}
          </Button>
        </div>
      </div>
    </section>;
}